import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import {
  createCollection,
  updateCollection,
} from "../../../../services/thunks/BusinessThunk";
import {
  BusinessCollectionDetail,
} from "../../../../interfaces/BusinessCollectionObject";
import {
  selectCreateCollectionStatus,
  selectUpdateCollectionStatus,
} from "../../../../dataManagement/uiDb/BusinessCollectionDb";
import { BusinessProfile } from "../../../../interfaces/BusinessObject";
import StorageApiService from "../../../../services/apiServices/StorageApiService";
import mediaUtility from "../../../../services/utility/MediaUtility";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import useScrollLock from "../../../../hooks/useScrollLock";
import CollectionBannerUploader from "./childComponents/CollectionBannerUploader";
import BusinessSearchAdd from "./childComponents/BusinessSearchAdd";
import "./styles/CreateCollectionModal.css";

interface CreateCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Present in edit mode — the collection plus its resolved member businesses. */
  editCollection?: BusinessCollectionDetail | null;
  /**
   * Fires after a successful create/update, before onClose. The updateCollection
   * response only carries businessIds (not resolved BusinessProfile records —
   * same sparse shape as the API's own create/update sample response), so the
   * slice can't repopulate the live page's business grid on its own. Callers
   * that render that grid (BusinessCollectionPage) pass a refetch here.
   */
  onSaved?: () => void;
}

const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  isOpen,
  onClose,
  editCollection = null,
  onSaved,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useNotification();

  const { isSubmitting: isCreating } = useSelector(selectCreateCollectionStatus);
  const { isSubmitting: isUpdating } = useSelector(selectUpdateCollectionStatus);
  const isSubmitting = isCreating || isUpdating;

  const isEditMode = !!editCollection;

  const [name, setName] = useState("");
  const [existingBannerUrl, setExistingBannerUrl] = useState<string | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [selectedBusinesses, setSelectedBusinesses] = useState<BusinessProfile[]>([]);

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    if (editCollection) {
      setName(editCollection.collection.name);
      setExistingBannerUrl(editCollection.collection.bannerUrl ?? null);
      setSelectedBusinesses(editCollection.businesses);
    } else {
      setName("");
      setExistingBannerUrl(null);
      setSelectedBusinesses([]);
    }
    setBannerFile(null);
  }, [isOpen, editCollection]);

  const handleAddBusiness = (business: BusinessProfile) => {
    setSelectedBusinesses((prev) =>
      prev.some((b) => b.businessId === business.businessId)
        ? prev
        : [...prev, business]
    );
  };

  const handleRemoveBusiness = (businessId: string) => {
    setSelectedBusinesses((prev) => prev.filter((b) => b.businessId !== businessId));
  };

  const validate = (): boolean => {
    if (!name.trim()) {
      triggerNotification("Collection name is required.", NotificationType.ERROR);
      return false;
    }
    if (!bannerFile && !existingBannerUrl) {
      triggerNotification("Add a banner image.", NotificationType.ERROR);
      return false;
    }
    // The backend errors out on an empty businessIds array (returns a 500 /
    // INTERNAL_ERROR rather than a clean validation rejection) — block it
    // client-side with an actionable message instead of letting that request
    // through and surfacing a generic "something went wrong".
    if (selectedBusinesses.length === 0) {
      triggerNotification("Add at least one business to the collection.", NotificationType.ERROR);
      return false;
    }
    return true;
  };

  /** Uploads the staged banner file and returns its permanent public URL. */
  const uploadBanner = async (file: File): Promise<string> => {
    const contentType = mediaUtility.resolveContentType(file);
    if (!contentType) {
      throw new Error("This image type isn't supported.");
    }
    setIsUploadingBanner(true);
    try {
      const target = await StorageApiService.requestUpload({
        fileName: file.name,
        folder: "business",
        contentType,
      });
      await StorageApiService.uploadToTarget(target, file, contentType);
      return target.publicUrl;
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate() || isSubmitting || isUploadingBanner) return;

    try {
      const bannerUrl = bannerFile ? await uploadBanner(bannerFile) : (existingBannerUrl as string);
      const payload = {
        collectionName: name,
        businessIds: selectedBusinesses.map((b) => b.businessId),
        bannerUrl,
      };

      if (isEditMode && editCollection) {
        await dispatch(
          updateCollection({ uid: editCollection.collection.uid, ...payload })
        ).unwrap();
        triggerNotification("Collection updated!", NotificationType.SUCCESS);
        onSaved?.();
        onClose();
      } else {
        const created = await dispatch(createCollection(payload)).unwrap();
        triggerNotification("Collection created!", NotificationType.SUCCESS);
        onSaved?.();
        onClose();
        // "Create" takes the lead straight to the new, live collection page.
        navigate(`/collection/${created.uid}`, { state: { collection: created } });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      triggerNotification(msg, NotificationType.ERROR);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-coll-overlay" onClick={onClose}>
      <div className="create-coll-modal" onClick={(e) => e.stopPropagation()}>
        <div className="create-coll-header">
          <button
            type="button"
            className="create-coll-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <h2 className="create-coll-title">
            {isEditMode ? "Edit Collection" : "Create Collection"}
          </h2>
          <div style={{ width: "2.125rem" }} />
        </div>

        <div className="create-coll-body">
          <div className="create-coll-field">
            <label className="create-coll-label">Name</label>
            <input
              type="text"
              className="create-coll-input"
              placeholder="e.g. Best Coffee Spots"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="create-coll-field">
            <label className="create-coll-label">Banner Image</label>
            <CollectionBannerUploader
              existingBannerUrl={existingBannerUrl}
              onFileSelected={setBannerFile}
              onInvalidFile={(reason) => triggerNotification(reason, NotificationType.ERROR)}
              isUploading={isUploadingBanner}
            />
          </div>

          <div className="create-coll-field">
            <label className="create-coll-label">Add Businesses</label>
            <BusinessSearchAdd
              selected={selectedBusinesses}
              onAdd={handleAddBusiness}
              onRemove={handleRemoveBusiness}
            />
          </div>
        </div>

        <div className="create-coll-footer">
          <button
            className="create-coll-submit"
            onClick={handleSubmit}
            disabled={isSubmitting || isUploadingBanner}
          >
            {isUploadingBanner
              ? "Uploading banner..."
              : isSubmitting
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
              ? "Save Changes"
              : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCollectionModal;
